'use client';

import { useQuery } from '@tanstack/react-query';
import { curatorQueries } from '../../query-factory';
import type {
  CampaignSnsRequirement,
  CuratorAccountStatus,
} from '../components/seedingDetail/recruit/useRecruitApplyValidation';
import type { SeedingCampaignDetail } from '../components/seedingDetail/types';

export interface SeedingApplyGuard {
  /** 신청/수락 검증에 넘길 큐레이터 계정 상태 (실 데이터). */
  accountStatus: CuratorAccountStatus;
  /** 캠페인 요구 SNS 채널 (없으면 undefined → SNS 검사 스킵). */
  snsRequirement: CampaignSnsRequirement | undefined;
  /** 큐레이터 shop slug (= userInfo.snsTitle). SNS 등록 페이지 이동(profileSns) 시 사용. */
  snsTitle: string;
  /** 검증에 필요한 카운트/프로필 조회가 모두 끝났는지 (조회 중엔 신청 보류 권장). */
  isReady: boolean;
}

/**
 * 시딩 신청/수락 검증용 큐레이터 계정 상태를 실 데이터로 조립한다.
 *
 * - `hasShippingAddress` / `hasPenalty` ← 상세(`detail`)가 이미 보유.
 * - `activeCampaignCount` ← 내 모집/제안 카운트의 (대기 + 진행) 합 (PRD §5-1 동시 10개).
 * - `registeredSnsPlatforms` ← 큐레이터 프로필 SNS 링크 플랫폼.
 * - `snsRequirement` ← 상세의 `requiredSnsChannels`(BE campaign.snsChannels).
 *
 * 이전 `?validate=` mock 빌더(`buildMockAccountStatusFromQuery` 등)를 대체한다.
 */
export const useSeedingApplyGuard = (
  detail: SeedingCampaignDetail | undefined,
): SeedingApplyGuard => {
  const recruitmentCount = useQuery(curatorQueries.campaigns.seedingMeRecruitmentCount());
  const proposalCount = useQuery(curatorQueries.campaigns.seedingMeProposalCount());

  const userInfo = useQuery(curatorQueries.profile.userInfo);
  const snsTitle = userInfo.data?.snsTitle ?? '';
  const profile = useQuery({
    ...curatorQueries.profile.profileBySnsTitle(snsTitle),
    enabled: Boolean(snsTitle),
  });

  // 동시 진행 캠페인 수 = 모집(대기+진행) + 제안(대기+진행). 완료/미선정/거절은 제외.
  const activeCampaignCount =
    (recruitmentCount.data ? recruitmentCount.data.pending + recruitmentCount.data.confirmed : 0) +
    (proposalCount.data ? proposalCount.data.pending + proposalCount.data.confirmed : 0);

  const registeredSnsPlatforms = (profile.data?.snsLinks ?? []).map((link) => link.platform);

  const accountStatus: CuratorAccountStatus = {
    hasPenalty: detail?.penalty != null,
    penaltyReason: detail?.penalty?.reason,
    activeCampaignCount,
    registeredSnsPlatforms,
    // adapter 가 미등록 시에만 registered=false 로 set. 미지정(legacy)은 등록된 것으로 간주.
    hasShippingAddress: detail?.shippingAddress?.registered !== false,
  };

  const requiredChannels = detail?.requiredSnsChannels ?? [];
  const snsRequirement: CampaignSnsRequirement | undefined =
    requiredChannels.length > 0 ? { requiredPlatforms: requiredChannels } : undefined;

  // 프로필 SNS 조회는 snsTitle 이 있어야 동작 — snsTitle 자체가 없으면 SNS 검사 스킵 대상.
  const profileReady = snsTitle ? profile.isSuccess : userInfo.isSuccess;
  const isReady = recruitmentCount.isSuccess && proposalCount.isSuccess && profileReady;

  return { accountStatus, snsRequirement, snsTitle, isReady };
};
